import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Users2, ArrowRight, Trophy, Sparkles } from "lucide-react";
import AIScoreBadge from "./AIScoreBadge";

/**
 * Recommandations IA inter-prospects.
 * Affiche les N prospects les plus similaires au prospect courant.
 *
 * Props :
 *   api          : axios instance déjà authentifiée (depuis SocieteContext)
 *   prospectId   : id du prospect cible
 *   defaultMode  : "all" | "winners" — si winners, ne retourne que les converti
 */
const STATUS_BADGE = {
  nouveau: { color: "#3b82f6" },
  contacte: { color: "#8b5cf6" },
  qualifie: { color: "#f7c935" },
  proposition: { color: "#f97316" },
  converti: { color: "#22c55e" },
  perdu: { color: "#ef4444" },
};

export default function SimilarProspectsCard({ api, prospectId, defaultMode = "all", testId = "similar-prospects-card" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(defaultMode);
  const [meta, setMeta] = useState({ matched: 0, total_candidates: 0 });

  useEffect(() => {
    if (!prospectId) return;
    setLoading(true);
    const load = async () => {
      try {
        const r = await api.get(
          `/crm/prospects/${prospectId}/similar?limit=5&only_winners=${mode === "winners" ? "true" : "false"}`,
        );
        setItems(r.data?.items || []);
        setMeta({ matched: r.data?.matched || 0, total_candidates: r.data?.total_candidates || 0 });
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, prospectId, mode]);

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={`${testId}-loading`}>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Recherche de profils similaires…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-violet-500/15 flex items-center justify-center">
            <Users2 className="w-4 h-4 text-violet-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Profils similaires</h3>
            <p className="text-[10px] text-slate-500">
              {mode === "winners"
                ? "Prospects similaires qui ont déjà signé"
                : "Prospects au profil proche dans votre base"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
          <button
            onClick={() => setMode("all")}
            className={`text-[11px] px-2.5 py-1 rounded-md transition-colors ${mode === "all" ? "bg-[#f7c935] text-[#14101c] font-bold" : "text-slate-400 hover:text-white"}`}
            data-testid="similar-mode-all"
          >
            <Sparkles className="w-3 h-3 inline mr-1" /> Tous
          </button>
          <button
            onClick={() => setMode("winners")}
            className={`text-[11px] px-2.5 py-1 rounded-md transition-colors ${mode === "winners" ? "bg-emerald-500 text-white font-bold" : "text-slate-400 hover:text-white"}`}
            data-testid="similar-mode-winners"
          >
            <Trophy className="w-3 h-3 inline mr-1" /> Ont signé
          </button>
        </div>
      </div>

      {!items.length ? (
        <div className="text-center py-6 text-xs text-slate-500" data-testid={`${testId}-empty`}>
          {mode === "winners"
            ? "Aucun prospect converti avec un profil similaire."
            : `Aucun prospect similaire trouvé parmi ${meta.total_candidates} candidat(s).`}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const sb = STATUS_BADGE[it.status] || { color: "#64748b" };
            return (
              <Link
                key={it.prospect_id}
                to={it.url}
                className="block bg-[#14101c] hover:bg-[#1a1525] border border-white/5 hover:border-white/15 rounded-lg p-3 transition-all hover:-translate-y-0.5 group"
                data-testid={`similar-prospect-${it.prospect_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{it.name}</span>
                      {it.status === "converti" && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold uppercase inline-flex items-center gap-1">
                          <Trophy className="w-2.5 h-2.5" /> Signé
                        </span>
                      )}
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: sb.color + "22", color: sb.color }}
                      >
                        {it.status}
                      </span>
                      {it.ai_tier && (
                        <AIScoreBadge tier={it.ai_tier} score={it.ai_score} size="sm" testId={`sim-ai-${it.prospect_id}`} />
                      )}
                    </div>
                    {it.company && <div className="text-[10px] text-slate-500 truncate mt-0.5">{it.company}</div>}
                    {it.common_attributes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {it.common_attributes.map((c, i) => (
                          <span
                            key={i}
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/15"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-violet-400">{Math.round((it.similarity || 0) * 100)}%</div>
                    <div className="text-[9px] text-slate-500">similarité</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors flex-shrink-0 self-center" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
